/**
 * P0 Alarm Banner Component
 * Global persistent banner that appears when API health checks are down.
 * Polls for active incidents every 15 seconds.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { Error as ErrorIcon, Close as CloseIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { addNotification } from '../../store/uiSlice';
import apiService from '../../services/api';

interface ActiveIncident {
  id: string;
  health_check_name: string;
  started_at: string;
}

const P0AlarmBanner: React.FC = () => {
  const [activeIncidents, setActiveIncidents] = useState<ActiveIncident[]>([]);
  const [dismissed, setDismissed] = useState(false);
  const seenIds = useRef(new Set<string>());
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    const fetchActiveIncidents = async () => {
      try {
        const res = await apiService.getApiActiveIncidents();
        const incidents: ActiveIncident[] = res?.data || [];
        setActiveIncidents(incidents);

        // Notify for new incidents only
        for (const inc of incidents) {
          if (!seenIds.current.has(inc.id)) {
            seenIds.current.add(inc.id);
            dispatch(addNotification({
              type: 'error',
              message: `P0 ALERT: ${inc.health_check_name} is DOWN`,
            }));
            // Re-show banner if it was dismissed and a new incident appears
            setDismissed(false);
          }
        }

        // Clean up seen IDs for resolved incidents
        const currentIds = new Set(incidents.map(i => i.id));
        seenIds.current.forEach(id => {
          if (!currentIds.has(id)) seenIds.current.delete(id);
        });
      } catch (err) {
        // Silently fail — don't spam errors for the banner
      }
    };

    fetchActiveIncidents();
    const interval = setInterval(fetchActiveIncidents, 15000);
    return () => clearInterval(interval);
  }, [dispatch]);

  if (activeIncidents.length === 0 || dismissed) return null;

  const names = activeIncidents.map(i => i.health_check_name).join(', ');

  return (
    <Box
      sx={{
        backgroundColor: '#d32f2f',
        color: 'white',
        px: 3,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        animation: 'p0pulse 2s infinite',
        '@keyframes p0pulse': {
          '0%': { backgroundColor: '#d32f2f' },
          '50%': { backgroundColor: '#b71c1c' },
          '100%': { backgroundColor: '#d32f2f' },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <ErrorIcon />
        <Typography variant="body1" fontWeight="bold">
          P0 ALERT: {activeIncidents.length} API health check{activeIncidents.length > 1 ? 's' : ''} DOWN — {names}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.1)' } }}
          onClick={() => navigate('/dashboard/monitoring/api')}
        >
          View Details
        </Button>
        <IconButton size="small" sx={{ color: 'white' }} onClick={() => setDismissed(true)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default P0AlarmBanner;
